
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import GlassCard from "@/components/ui/glass-card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  DollarSign, 
  MapPin, 
  TrendingUp, 
  InfoIcon
} from "lucide-react";
import { toast } from "sonner";

type InsightItem = {
  title: string;
  icon: string;
  content: string;
  color: string;
};

type InsightsData = {
  investmentStrategy: InsightItem;
  geographicFocus: InsightItem;
  timingOptimization: InsightItem;
};

const AIInsights = ({ forms, leads }: { forms: any[]; leads: any[] }) => {
  const { data: insightsData, isLoading, error } = useQuery({
    queryKey: ['aiInsights', forms.length, leads.length],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('generate-insights', {
          body: { forms, leads }
        });

        if (error) throw new Error(error.message);
        return data.insights as InsightsData;
      } catch (err: any) {
        console.error('Error fetching AI insights:', err);
        toast.error('Failed to generate AI insights');
        throw err;
      }
    },
    enabled: forms.length > 0 || leads.length > 0,
    staleTime: 1000 * 60 * 15, // 15 minutes
    retry: 1,
  });

  // Helper function to render the correct icon
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'dollar':
        return <DollarSign className="h-6 w-6" />;
      case 'map-pin':
        return <MapPin className="h-6 w-6" />;
      case 'trending-up':
        return <TrendingUp className="h-6 w-6" />;
      default:
        return <InfoIcon className="h-6 w-6" />;
    }
  };

  // Helper function to get the right color class
  const getColorClass = (color: string) => {
    const colorMap: Record<string, string> = {
      'blue': 'bg-blue-100 dark:bg-blue-950',
      'green': 'bg-green-100 dark:bg-green-950',
      'amber': 'bg-amber-100 dark:bg-amber-950',
    };
    return colorMap[color] || 'bg-gray-100 dark:bg-gray-800';
  };

  const getIconColorClass = (color: string) => {
    const colorMap: Record<string, string> = {
      'blue': 'bg-blue-500',
      'green': 'bg-green-500',
      'amber': 'bg-amber-500',
    };
    return colorMap[color] || 'bg-gray-500';
  };

  // If there are no leads but there are forms, still show something meaningful
  const shouldShowNoDataMessage = !isLoading && !error && (!insightsData || Object.keys(insightsData).length === 0);

  return (
    <GlassCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full p-6 mt-8"
      hover={false}
      variant="default"
    >
      <div className="mb-4">
        <h2 className="text-2xl font-bold">AI-Driven Investment Insights 💡</h2>
      </div>
      
      <p className="text-muted-foreground mb-4">
        Our AI engine analyzes your potential investor data to identify key opportunities
      </p>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg">
          Unable to generate insights at this time. Please try again later.
        </div>
      ) : shouldShowNoDataMessage ? (
        <div className="p-6 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-lg">
          <p className="font-medium mb-2">No investor data available for analysis</p>
          <p className="text-sm">Collect lead data through your investment forms to receive personalized insights about your potential investors.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {insightsData && Object.entries(insightsData).map(([key, insight]) => (
            <div 
              key={key} 
              className={`rounded-lg ${getColorClass(insight.color)} p-5`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full ${getIconColorClass(insight.color)} flex items-center justify-center text-white`}>
                  {renderIcon(insight.icon)}
                </div>
                <h3 className="font-bold text-lg">{insight.title}</h3>
              </div>
              <p>{insight.content}</p>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default AIInsights;
